import { mintOauthRedirectApi } from "@/api/services/admin/admin-user";
import { Button } from "@/components/elements/Button";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { notifyProductHandoff } from "@/utils/productHandoff";
import { getTokens } from "@/utils/token";
import { isValidRedirectUrl, redirectToLogin } from "@/utils/url";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";

/**
 * Single hand-off point for every product SSO redirect: the opener sends its shared product tab here
 * with { organizationId, productCode }, this page mints a fresh redirect and navigates on.
 *
 * Minting lives here rather than in the opener because the handoff token is single-use and expires
 * after 60s server-side — issuing it immediately before navigating keeps it well inside that window,
 * and the opener no longer needs a reserved blank tab to dodge the popup blocker.
 */
const MintOauthRedirectPage: FC = () => {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("organizationId");
  const productCode = searchParams.get("productCode");

  // Guards React StrictMode's double effect: two concurrent mints on a not-yet-provisioned admin
  // race each other server-side (the second write orphans the first product account).
  const isMintingRef = useRef(false);
  const [hasFailed, setHasFailed] = useState(false);

  const mint = useCallback(async () => {
    if (!organizationId || !productCode) return;
    if (isMintingRef.current) return;
    isMintingRef.current = true;

    const { refreshToken } = getTokens();
    if (!refreshToken) {
      redirectToLogin();
      return;
    }

    try {
      const { data } = await mintOauthRedirectApi({ organizationId: Number(organizationId), productCode, refreshToken });
      if (!isValidRedirectUrl(data.redirectURL)) {
        throw new Error("Invalid redirect target");
      }
      // The opener never sees the provisioning result (it happened in this tab), so tell it to refetch.
      notifyProductHandoff({ organizationId, productCode });
      globalThis.location.replace(data.redirectURL);
    } catch (error) {
      showError(error);
      setHasFailed(true);
      isMintingRef.current = false;
    }
  }, [organizationId, productCode]);

  useEffect(() => {
    mint();
  }, [mint]);

  // Entered without a target (e.g. a stale bookmark) — nothing to hand off.
  if (!organizationId || !productCode) {
    return <Navigate to={AppRoutes.home} replace />;
  }

  if (hasFailed) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" gap={3} px={3}>
        <Typography variant="body14" color={text.secondary} textAlign="center">
          プロダクトへの移動に失敗しました。
        </Typography>
        <Button
          variant="secondary"
          onClick={() => {
            setHasFailed(false);
            mint();
          }}
        >
          <Typography variant="body14Semi">再試行</Typography>
        </Button>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" height="100vh" gap={2}>
      <CircularProgress />
      <Typography variant="body14" color={text.secondary}>
        プロダクトへ移動中...
      </Typography>
    </Stack>
  );
};

export default MintOauthRedirectPage;
